import React from "react";
import Layout from "../central/Layout";
import Panel from "../central/Panel";
import { HeadingSmall, BodyText } from "../central/Typography";
import theme from "../central/theme";
import { useNavigate } from "react-router-dom";
import Button from "../central/Button";
import { useAuth } from "../context/AuthContext";
import { buildFeedbackUrl } from "../utils/feedback";
import { buildBugReportUrl } from "../utils/bugReporter";

const HelpContact = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const supportEmail = "support@clubweek.co";

  const styles = {
    pageWrap: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing.medium,
    },
    betaBanner: {
      background: "rgba(0,0,0,0.03)",
      border: "1px solid rgba(0,0,0,0.06)",
      padding: theme.spacing.medium,
    },
    bannerTitleRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: theme.spacing.small,
      marginBottom: theme.spacing.small,
    },
    pillRow: {
      display: "flex",
      flexWrap: "wrap",
      gap: 8,
      marginTop: theme.spacing.small,
    },
    pill: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "6px 10px",
      borderRadius: 999,
      fontSize: 12,
      border: "1px solid rgba(0,0,0,0.08)",
      background: "rgba(255,255,255,0.9)",
      lineHeight: "14px",
      whiteSpace: "nowrap",
    },
    pillLive: {
      background: "rgba(34,197,94,0.08)",
      border: "1px solid rgba(34,197,94,0.25)",
      color: "#166534",
    },
    pillProgress: {
      background: "rgba(59,130,246,0.08)",
      border: "1px solid rgba(59,130,246,0.25)",
      color: "#1e3a8a",
    },
    pillRoadmap: {
      background: "rgba(124,58,237,0.08)",
      border: "1px solid rgba(124,58,237,0.25)",
      color: "#4c1d95",
    },
    grid: {
      display: "grid",
      gridTemplateColumns: "1fr",
      gap: theme.spacing.medium,
    },
    card: {
      backgroundColor: theme.colors.surface,
      padding: theme.spacing.medium,
      border: "1px solid rgba(0,0,0,0.06)",
    },
    cardHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: theme.spacing.small,
      marginBottom: theme.spacing.small,
    },
    sectionTitle: {
      marginBottom: theme.spacing.small,
      fontWeight: "bold",
    },
    list: {
      margin: 0,
      paddingLeft: 14,
    },
    listItem: {
      marginBottom: 6,
    },
    subtle: {
      opacity: 0.75,
    },
    buttonStack: {
      display: "flex",
      flexDirection: "column",
      gap: 10,
      marginBottom: theme.spacing.small,
    },
  };

  return (
    <Layout>
      {/* Small internal style block just for basic responsiveness */}
      <style>{`
        @media (min-width: 900px) {
          .helpContactGrid {
            grid-template-columns: 1fr 1fr;
          }
        }
        a.underlineLink {
          text-decoration: underline;
          color: inherit;
        }
      `}</style>

      <div style={styles.pageWrap}>
        {/* Top banner */}
        <Panel style={styles.betaBanner}>
          <div style={styles.bannerTitleRow}>
            <HeadingSmall style={{ marginBottom: 0, fontWeight: "bold" }}>Help & Support</HeadingSmall>
            <BodyText style={{ marginBottom: 0, ...styles.subtle }}>Private beta</BodyText>
          </div>

          <BodyText style={{ marginBottom: 0 }}>
            Quick help, links, and contact details.
            <br />
            If something looks wrong, please report it. Bugs will be fixed quickly during beta.
          </BodyText>

        </Panel>

        {/* Cards */}
        <div className="helpContactGrid" style={styles.grid}>
          <Panel style={styles.card}>
            <div style={styles.cardHeader}>
              <HeadingSmall style={{ marginBottom: 0, fontWeight: "bold" }}>Quick actions</HeadingSmall>
            </div>

            <div style={styles.buttonStack}>
              <Button
                onClick={() => {
                  const url = buildBugReportUrl({
                    leagueID: currentUser?.currentLeagueID || "",
                    userID: currentUser?.uid || "",
                    role:
                      (currentUser?.roles && currentUser?.currentLeagueID
                        ? currentUser.roles[currentUser.currentLeagueID]
                        : "") || "",
                    source: "help",
                  });
                  window.open(url, "_blank", "noopener,noreferrer");
                }}
                primary
              >
                Report a Bug
              </Button>
              <Button
                onClick={() => {
                  const url = buildFeedbackUrl({
                    leagueID: currentUser?.currentLeagueID || "",
                    userID: currentUser?.uid || "",
                    role:
                      (currentUser?.roles && currentUser?.currentLeagueID
                        ? currentUser.roles[currentUser.currentLeagueID]
                        : "") || "",
                    source: "help",
                  });
                  window.open(url, "_blank", "noopener,noreferrer");
                }}
              >
                Send Feedback (30 sec)
              </Button>
            </div>
          </Panel>

          <Panel style={styles.card}>
            <div style={styles.cardHeader}>
              <HeadingSmall style={{ marginBottom: 0, fontWeight: "bold" }}>Contact</HeadingSmall>
            </div>

            <BodyText style={{ marginBottom: 6 }}>
              Email:{" "}
              <a href={`mailto:${supportEmail}`} className="underlineLink">
                {supportEmail}
              </a>
            </BodyText>
            <BodyText style={{ marginBottom: 6 }}>Response time: usually within 24 hours.</BodyText>
            <BodyText>If it’s urgent, message your league admin as well.</BodyText>
          </Panel>

          <Panel style={styles.card}>
            <div style={styles.cardHeader}>
              <HeadingSmall style={{ marginBottom: 0, fontWeight: "bold" }}>FAQ / How it works</HeadingSmall>
            </div>

            <BodyText style={{ marginBottom: theme.spacing.small }}>A few common questions:</BodyText>
            <ul style={styles.list}>
              <li style={styles.listItem}>How do deadlines work?</li>
              <li style={styles.listItem}>Why can’t I edit after the deadline?</li>
              <li style={styles.listItem}>How do transfers and chips work?</li>
              <li style={styles.listItem}>How do I join a league?</li>
            </ul>
            <Button onClick={() => navigate("/faq")} style={{ marginTop: theme.spacing.medium }}>
              Open FAQ
            </Button>
          </Panel>

          <Panel style={styles.card}>
            <div style={styles.cardHeader}>
              <HeadingSmall style={{ marginBottom: 0, fontWeight: "bold" }}>About ClubWeek</HeadingSmall>
            </div>

            <BodyText style={{ marginBottom: theme.spacing.small }}>
              ClubWeek was built by Alex Shoff, originally as an internal fantasy game for his own 7‑a‑side team in New York’s Bowery League.
            </BodyText>

            <BodyText style={{ marginBottom: theme.spacing.small }}>
              The idea was to create a simple way to add an extra competitive layer to real‑life football, bringing in extra stakes without replacing the actual game and improving team adhesion in casual leagues.
            </BodyText>

            <BodyText style={{ marginBottom: theme.spacing.small }}>
              It’s early‑stage, actively evolving, and shaped directly by feedback from the teams using it.
            </BodyText>
          </Panel>
        </div>
      </div>
    </Layout>
  );
};

export default HelpContact;
